import { css } from '@compiled/react';
import { useLazyLoadQuery } from 'react-relay';
import { usePathParam, useRouterActions } from 'react-resource-router';
import { ProfileHeaderQuery } from 'graphql/__generated__/ProfileHeaderQuery.graphql';
import { PROFILE_HEADER_QUERY } from 'graphql/ProfileHeaderQuery';
import { token } from '@atlaskit/tokens';
import { getHighResolutionAvatar } from 'utils/helper';
import CameraIcon from '@atlaskit/icon/glyph/camera';
import { Suspense, useCallback, useContext } from 'react';
import useIsPersonalView from 'utils/useIsPersonalView';
import { Button, ButtonAppearance } from '@atlassian/learning-components';
import { AppContext } from 'contexts/app';
import {
  trackCameraButtonClick,
  trackEditProfileButtonClick,
} from 'analytics/profileHeaderAnalytics';
import { StatsSnapshotQuery } from 'graphql/__generated__/StatsSnapshotQuery.graphql';
import { STATS_SNAPSHOT_QUERY } from 'graphql/StatsSnapshotQuery';
import { RankLozenge } from './RankLozenge';
import { CommunityRank } from './types';
import { getForumsSignInUrl } from 'utils/url-generator';
import useIsProfileBanned from 'utils/useIsProfileBanned';
import useIsLoggedInUserAForumsModerator from 'utils/useIsLoggedInUserAForumsModerator';
import useIsLoggedIn from 'utils/useIsLoggedIn';
import useLoggedInUser from 'utils/useLoggedInUser';
import { ATLASSIAN_ACCOUNT_ANY_USER_QUERY } from 'graphql/AtlassianAccountAnyUserQuery';
import { AtlassianAccountAnyUserQuery } from 'graphql/__generated__/AtlassianAccountAnyUserQuery.graphql';
import { IMAGE_SIZE } from 'constants/index';

const ProfileHeaderContent = () => {
  const [pathProfileId] = usePathParam('pathProfileId');
  const isPersonalView = useIsPersonalView(pathProfileId as string);
  // const isPersonalView = true;

  const { push } = useRouterActions();
  const profileResponseData = useLazyLoadQuery<ProfileHeaderQuery>(
    PROFILE_HEADER_QUERY,
    { internalId: pathProfileId as string },
    { fetchPolicy: 'store-or-network' },
  );
  const statsSnapshotResponse = useLazyLoadQuery<StatsSnapshotQuery>(
    STATS_SNAPSHOT_QUERY,
    { internalId: pathProfileId as string },
    { fetchPolicy: 'store-or-network' },
  );

  const isProfileBanned = useIsProfileBanned(pathProfileId as string);
  const profileData = profileResponseData?.unified?.unifiedProfile;
  if (!profileData) {
    return <div>{'Could not fetch profile data'}</div>;
  }

  const rawRank =
    statsSnapshotResponse?.unified?.unifiedProfile?.forums?.snapshot?.rank;
  const rank =
    rawRank?.toUpperCase() === 'NOT APPLICABLE' ? 'VISITOR' : rawRank;
  const khorosId =
    statsSnapshotResponse?.unified?.unifiedProfile?.forums?.snapshot?.id || '';

  const userResponseData = useLazyLoadQuery<AtlassianAccountAnyUserQuery>(
    ATLASSIAN_ACCOUNT_ANY_USER_QUERY,
    { accountId: profileData.aaid as string },
    { fetchPolicy: 'store-or-network' },
  );
  const userData = userResponseData.user;
  if (!userData) {
    return <div>{'Could not fetch user data'}</div>;
  }

  // data of current logged-in user (not profile user)
  const { email } = useLoggedInUser() || {};
  const isLoggedIn = useIsLoggedIn();
  const isLoggedUserAForumsModerator = useIsLoggedInUserAForumsModerator();

  const handleCameraButtonClick = () => {
    trackCameraButtonClick(isPersonalView);
    handleEditProfile();
  };

  const handleEditProfileButtonClick = () => {
    trackEditProfileButtonClick(isPersonalView);
    handleEditProfile();
  };

  const handleEditProfile = useCallback(() => {
    push(`/profile/${pathProfileId}/edit`);
  }, [pathProfileId]);

  // temp fix for forums user id
  const forumsNodeId =
    statsSnapshotResponse?.unified?.unifiedProfile?.forums?.id;
  const forumsUserIdParts = (forumsNodeId ?? '').split('::forums/');
  const forumsUserId = forumsUserIdParts?.[1] ?? '';

  const handleModeratorControlsClick = useCallback(() => {
    const moderatorUrl =
      window.location.origin +
      `/forums/user/viewprofilepage/user-id/${forumsUserId}?show=admin#details`;
    const effectiveModeratorUrl = isLoggedIn
      ? getForumsSignInUrl({
          redirectUrl: encodeURIComponent(moderatorUrl),
          email: email || '',
        })
      : moderatorUrl;
    window.location.href = effectiveModeratorUrl;
  }, [khorosId]);

  const { nickname, picture } = userData;
  const nicknameLength = nickname ? nickname.length : 0;

  const { role, company, location } = profileData;
  const userPosition = [];
  if (role) {
    userPosition.push(role);
  }
  if (company) {
    userPosition.push(company);
  }

  let highResolutionPicture = '';
  try {
    const { profileAvatar } = useContext(AppContext);
    highResolutionPicture = getHighResolutionAvatar(
      picture,
      IMAGE_SIZE.LARGE,
      profileAvatar,
    );
  } catch (e) {
    console.error('Error in changing to high resolution picture', e);
  }

  const nameOnly = !location && !userPosition?.length;

  // const screenType = useScreenType();

  return (
    <>
      <div css={profilePhotoContainerStyles}>
        <img
          css={profilePhotoStyles}
          src={highResolutionPicture}
          alt="Profile Photo"
        />
        {isPersonalView &&
        (!isProfileBanned || isLoggedUserAForumsModerator) ? (
          <button
            type="button"
            css={[
              cameraIconEditButtonStyles,
              isProfileBanned &&
                !isLoggedUserAForumsModerator &&
                hideCameraButtonStyles,
            ]}
            onClick={handleCameraButtonClick}
            data-testid="camera-edit-button"
          >
            <CameraIcon
              primaryColor={token('color.icon.subtle')}
              // size={screenType === 'tablet' ? 'large' : 'xlarge'}
              size={'large'}
              label="Edit profile"
            />
          </button>
        ) : null}
      </div>

      <div
        css={[
          mainContentStyles,
          nameOnly && mainContentPadding.nameOnly,
          !nameOnly && mainContentPadding.default,
        ]}
      >
        <span css={[headingStyles, nameStyles]}>{nickname}</span>
        {isProfileBanned && !isLoggedUserAForumsModerator && (
          <RankLozenge rank={'BANNED'} nicknameLength={nicknameLength} />
        )}
        {rank && (!isProfileBanned || isLoggedUserAForumsModerator) && (
          <RankLozenge
            rank={
              (rank === 'VISITOR'
                ? 'VISITOR'
                : rank.toUpperCase()) as CommunityRank
            }
            nicknameLength={nicknameLength}
          />
        )}
        {userPosition?.length ? (
          <h5 css={[headingStyles, positionStyles]}>
            {userPosition.join(' at ')}
          </h5>
        ) : null}
        {location ? (
          <h5 css={[headingStyles, locationStyles]}>{location}</h5>
        ) : null}
      </div>

      {isPersonalView ? (
        <div
          css={[editButtonWrapperStyles, nameOnly && nameOnlyEditButtonStyles]}
        >
          {isLoggedUserAForumsModerator ? (
            <div css={[editButtonWrapperStyles, moderatorControlsButton]}>
              <Button
                appearance={ButtonAppearance.PRIMARY}
                onClick={handleModeratorControlsClick}
                label={'Moderator controls'}
              />
            </div>
          ) : null}

          {!isProfileBanned || isLoggedUserAForumsModerator ? (
            <Button
              appearance={
                isLoggedUserAForumsModerator
                  ? ButtonAppearance.SECONDARY
                  : ButtonAppearance.PRIMARY
              }
              onClick={handleEditProfileButtonClick}
              label={'Edit profile'}
            />
          ) : null}
        </div>
      ) : null}
    </>
  );
};

const LoadingProfileHeader = () => {
  return (
    <>
      <div css={profilePhotoContainerStyles}>
        <div css={[profilePhotoStyles, loadingProfilePhotoStyles]} />
      </div>
      <div
        css={[
          mainContentStyles,
          mainContentPadding.default,
          loadingProfileInfoStyles,
        ]}
      />
    </>
  );
};

const ProfileHeader = () => {
  return (
    <header css={headerStyles}>
      <div css={containerStyles}>
        <Suspense fallback={<LoadingProfileHeader />}>
          <ProfileHeaderContent />
        </Suspense>
      </div>
    </header>
  );
};

export default ProfileHeader;

// DESKTOP_PHOTO_SIZE  renders to (accounting for margin) 245px x 245px
const DESKTOP_PHOTO_SIZE = 261;
// TABLET_PHOTO_SIZE renders to (accounting for margin) 185px x 185px
const TABLET_PHOTO_SIZE = 195;
// MOBILE_PHOTO_SIZE renders to (accounting for margin) 105px x 105px
const MOBILE_PHOTO_SIZE = 111;
const DESKTOP_PHOTO_BORDER_SIZE = 8;
const TABLET_PHOTO_BORDER_SIZE = 5;
const MOBILE_PHOTO_BORDER_SIZE = 3;

const headerStyles = css({
  display: 'block',
  position: 'relative',
  width: '100%',
});

const hideCameraButtonStyles = css({
  display: 'none',
});

const containerStyles = css({
  display: 'flex',
  fontSize: '24px',
  width: '100%',
  justifyContent: 'space-between',
  '@media (max-width: 767px)': {
    flex: 1,
    padding: '0 16px',
    flexDirection: 'column',
  },
  '@media (min-width: 768px) and (max-width: 1279px)': {
    flexWrap: 'wrap',
  },
  '@media (min-width: 1280px)': {
    minHeight: '145px',
  },
});

const profilePhotoStyles = css({
  width: '100%',
  borderRadius: '100%',
  height: '100%',
  border: `${MOBILE_PHOTO_BORDER_SIZE}px solid ${token(
    'color.background.input',
  )}`,
  '@media (min-width: 768px)': {
    border: `${TABLET_PHOTO_BORDER_SIZE}px solid ${token(
      'color.background.input',
    )}`,
  },
  '@media (min-width: 1280px)': {
    border: `${DESKTOP_PHOTO_BORDER_SIZE}px solid ${token(
      'color.background.input',
    )}`,
  },
});
const moderatorControlsButton = css({
  paddingTop: '0px',
});

const profilePhotoContainerStyles = css({
  width: `${MOBILE_PHOTO_SIZE}px`,
  height: `${MOBILE_PHOTO_SIZE}px`,
  flexGrow: 0,
  flexShrink: 0,
  position: 'relative',
  '@media (max-width: 767px)': {
    display: 'block',
    margin: `calc(-${MOBILE_PHOTO_SIZE}px / 2) auto 8px auto`,
  },
  '@media (min-width: 768px) and (max-width: 1280px)': {
    marginTop: '-69px',
    width: `${TABLET_PHOTO_SIZE}px`,
    height: `${TABLET_PHOTO_SIZE}px`,
  },
  '@media (min-width: 1280px)': {
    marginTop: '-106px',
    width: `${DESKTOP_PHOTO_SIZE}px`,
    height: `${DESKTOP_PHOTO_SIZE}px`,
  },
});

const mainContentStyles = css({
  display: 'flex',
  flexDirection: 'row-reverse',
  flexWrap: 'wrap',
  justifyContent: 'flex-end',
  flexGrow: 1,
  flexShrink: 1,
  '@media (max-width: 768px)': {
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
  },
  '@media (min-width: 768px)': {
    marginLeft: '24px',
    gap: '8px',
  },
  '@media (min-width: 768px) and (max-width: 1279px)': {
    //24px is margin of the mainContentStyles
    maxWidth: `calc(100% - ${TABLET_PHOTO_SIZE}px - 24px)`,
  },
});

const mainContentPadding = {
  default: css({
    '@media (max-width: 768px)': {
      padding: `${token('space.100')} 0`,
    },
    '@media (min-width: 768px)': {
      padding: `${token('space.200')} 0`,
    },
    '@media (min-width: 1280px)': {
      padding: `${token('space.300')} 0`,
    },
  }),
  nameOnly: css({
    '@media (min-width: 768px) and (max-width: 1279px)': {
      padding: `${token('space.200')} 0`,
    },
    '@media (min-width: 1280px)': {
      paddingTop: token('space.600'),
    },
  }),
};

const nameStyles = css({
  fontSize: token('space.400'),
  fontWeight: token('font.weight.bold'),
  lineHeight: '40px',
  letterSpacing: '-.3%',
  color: token('color.text'),
  marginRight: '8px',
  wordBreak: 'break-word',
  flexBasis: 'fit-content',
  '@media (max-width: 768px)': {
    flexBasis: '100%',
    textAlign: 'center',
    marginRight: '0px',
  },
  '@media (min-width: 768px)': {
    fontSize: '40px',
    lineHeight: token('space.600'),
  },
});

const headingStyles = css({
  fontFamily: token('font.family.brand.heading'),
  wordWrap: 'break-word',
});

const positionStyles = css({
  fontSize: token('space.200'),
  lineHeight: token('space.300'),
  fontWeight: token('font.weight.semibold'),
  margin: 0,
  flexBasis: '100%',
  color: token('color.text'),
  '@media (min-width: 768px)': {
    fontSize: '20px',
    marginBottom: '-8px',
    lineHeight: '28px',
  },
});

const locationStyles = css({
  fontSize: token('space.200'),
  lineHeight: token('space.300'),
  fontWeight: token('font.weight.semibold'),
  margin: 0,
  flexBasis: '100%',
  color: token('color.text.subtle'),
  '@media (min-width: 768px)': {
    fontSize: '20px',
    lineHeight: '28px',
  },
});

const editButtonWrapperStyles = css({
  flexGrow: 0,
  flexShrink: 0,
  alignSelf: 'flex-start',
  padding: '24px 0',
  '@media (max-width: 767px)': {
    alignSelf: 'center',
    paddingBottom: '0px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '24px',
  },
  '@media (min-width: 768px) and (max-width: 1279px)': {
    width: '100%',
  },
});

const nameOnlyEditButtonStyles = css({
  padding: '48px 0',
  '@media (max-width: 767px)': {
    padding: '24px 0',
  },
  '@media (min-width: 768px) and (max-width: 1279px)': {
    padding: '24px 0',
  },
});

const cameraIconEditButtonStyles = css({
  cursor: 'pointer',
  width: '62px',
  height: '62px',
  borderRadius: '100%',
  backgroundColor: token('color.background.input'),
  filter: `drop-shadow(0px 1px 1px rgba(9, 30, 66, 0.25)) drop-shadow(0px 0px 1px rgba(9, 30, 66, 0.31))`,
  position: 'absolute',
  bottom: '0',
  right: '0',
  border: 'none',
  '@media (min-width: 1280px)': {
    bottom: `${DESKTOP_PHOTO_BORDER_SIZE}px`,
    right: `${DESKTOP_PHOTO_BORDER_SIZE}px`,
  },
  '@media (min-width: 768px) and (max-width: 1279px)': {
    width: '55px',
    height: '55px',
    bottom: `${TABLET_PHOTO_BORDER_SIZE}px`,
    right: `${TABLET_PHOTO_BORDER_SIZE}px`,
  },
  '@media (max-width: 767px)': {
    display: 'none',
  },
});

const loadingProfilePhotoStyles = css({
  background: token('color.background.accent.gray.subtlest'),
});

const loadingProfileInfoStyles = css({
  marginRight: '35%',
  height: '48px',
  borderRadius: '8px',
  marginTop: '8px',
  background: token('color.background.accent.gray.subtlest'),
  '@media (max-width: 767px)': {
    marginLeft: '15%',
    marginRight: '15%',
  },
});
